import React, { Component } from 'react';
import { connect } from 'react-redux';

function getCapitalizedName(name) {
  if (typeof name !== 'string' || name.length === 0) {
    return name;
  }
  return `${name.charAt(0).toUpperCase()}${name.substring(1)}`;
}

function isObject(value) {
  return Object(value) === value;
}

function isObjectSubsetOf(first, second) {
  return Object.keys(first).every(key => first[key] === second[key]);
}

function equals(first, second) {
  if (isObject(first) && isObject(second)) {
    return isObjectSubsetOf(first, second) && isObjectSubsetOf(second, first);
  }
  return first === second;
}

export default function injectResource(TargetComponent, Schema, resource, descriptor, name) {
  function getProps(props) {
    const ret = descriptor.props(props);
    Object.keys(ret).forEach(key => {
      if (key === 'options') {
        return;
      }
      if (typeof ret[key] === 'function') {
        ret[key] = ret[key]();
      }
    });
    return ret;
  }

  function getOptions(props) {
    if (typeof descriptor.options === 'function') {
      return descriptor.options(props);
    }
    return {};
  }

  function getQuery(props) {
    if (typeof descriptor.query === 'function') {
      return descriptor.query(props) || {};
    }
    if (isObject(descriptor.query)) {
      return descriptor.query;
    }
    return {};
  }

  function mapStateToProps(state, props) {
    const store = state[name];

    if (!store && typeof resource.storeSelector !== 'function') {
      return {};
    }

    const mapping = {};
    const desired = getProps(props);
    const options = getOptions(props);

    Object.keys(desired).forEach(key => {
      let stateId = desired[key];

      if (typeof resource.stateId === 'function') {
        stateId = resource.stateId(stateId, options);
      }

      let value;

      if (typeof resource.storeSelector === 'function') {
        value = resource.storeSelector(name, stateId)(state);
      } else {
        value = store.get(stateId);
      }

      if (typeof resource.transform === 'function') {
        value = resource.transform(value, options);
      }

      mapping[key] = value;
    });

    return mapping;
  }

  function mapDispatchToProps(dispatch, props) {
    // eslint-disable-next-line no-useless-catch
    try {
      const mapping = {};
      const desired = getProps(props);
      const query = getQuery(props);

      // TODO: Here we need to generate other crud actions
      //       we might want to perform and name them accordingly
      Object.keys(desired).forEach(propName => {
        const capitalized = getCapitalizedName(propName);

        if (typeof resource.mapDispatchToProps === 'function') {
          Object.assign(
            mapping,
            resource.mapDispatchToProps({
              id: desired[propName],
              props: desired,
              propName,
              options: query,
              resource,
              dispatch,
              action(type) {
                return `${type}${capitalized}`;
              },
            })
          );
        } else {
          mapping[`read${capitalized}`] = () => {
            dispatch(resource.read(desired[propName], getOptions(props))).catch(error => {
              throw error;
            });
          };
        }
      });

      return mapping;
    } catch (e) {
      throw e;
    }
  }

  function shouldAwaitEntities(props) {
    return !Object.keys(getProps(props)).every(key => {
      return props[key] !== undefined;
    });
  }

  return @connect(
    mapStateToProps,
    mapDispatchToProps
  )
  class ResourceConnector extends Component {
    UNSAFE_componentWillMount() {
      Object.keys(getProps(this.props)).forEach(propName => {
        const capitalized = getCapitalizedName(propName);
        const read = this.props[`read${capitalized}`];
        if (typeof read === 'function') {
          read();
        }
      });
    }

    /**
     * If a component receives a new set of properties, for example if we
     * switch a page, we need to trigger a new read if any of the target
     * properties have changed. If we are mapping a channel resource to the
     * id key in the props, then we need to trigger a new read operation if
     * the key has updated.
     */
    componentDidUpdate(prevProps) {
      const previous = getProps(prevProps);
      const current = getProps(this.props);
      const prevQuery = getQuery(prevProps);
      const newQuery = getQuery(this.props);
      const hasQueryChanged = !equals(prevQuery, newQuery);

      Object.keys(current).forEach(key => {
        const previousValue = previous[key];
        const currentValue = current[key];
        const hasValueChanged = !equals(previousValue, currentValue);

        if (hasQueryChanged || hasValueChanged) {
          const doRead = this.props[`read${getCapitalizedName(key)}`];
          if (typeof doRead === 'function') {
            doRead();
          }
        }
      });
    }

    render() {
      if (shouldAwaitEntities(this.props)) {
        return null;
      }

      return <TargetComponent {...this.props} />;
    }
  };
}
